import * as Yup from "yup";
import { CLIENTS_URL } from "../routes";
import PageResponse from "../PageResponse";
import { ClientFilter } from "./ClientFilter";
import PageRequest from "../PageRequest";
import EntityId from "../EntityId";
import qs from "qs";
import { format } from "date-fns";
import httpClient from "../config/HttpClient";

export const PARTIAL_STATUS = "PARTIAL";

export interface ClientData {
  id?: string;
  addressee: string;
  companyTitle: string;
  companyCode: string;
  companyVatCode: string;
  companyAddress: string;
  companyPhone: string;
  emails: string[];
  validFrom: string;
  validTo: string;
  clientState: string;
  mainEmail?: string;
  comment: string;
  responsibleUser?: string;
  responsibleUserName: string;
}

export const defaultClientData: Readonly<ClientData> = {
  addressee: "",
  companyTitle: "",
  companyCode: "",
  companyVatCode: "",
  companyAddress: "",
  companyPhone: "",
  emails: [""],
  validFrom: "",
  validTo: "",
  clientState: "",
  comment: "",
  responsibleUser: "",
  responsibleUserName: "",
};

export const clientSchema = Yup.object().shape({
  addressee: Yup.string().required("Įmonės adresatas yra privalomas"),
  companyTitle: Yup.string().required("Įmonės pavadinimas yra privalomas"),
  companyCode: Yup.string().required("Įmonės kodas yra privalomas"),
  companyVatCode: Yup.string().required("Įmonės PVM kodas yra privalomas"),
  companyAddress: Yup.string().required("Įmonės adresas yra privalomas"),
  companyPhone: Yup.string().required("Telefono numeris yra privalomas"),
  clientState: Yup.string()
    .required("Statusas yra privalomas")
    .notOneOf([PARTIAL_STATUS], "Netinkamas statusas"),
  emails: Yup.array<String>().min(
    1,
    "Bent vienas el. pašto adresas yra reikalingas"
  ),
});

class ClientModel implements ClientData {
  id?: string;
  addressee: string = "";
  companyTitle: string = "";
  companyCode: string = "";
  companyVatCode: string = "";
  companyAddress: string = "";
  companyPhone: string = "";
  emails: string[] = [""];
  validFrom: string = "";
  validTo: string = "";
  clientState: string = "";
  mainEmail?: string;
  comment: string = "";
  responsibleUser?: string = "";
  responsibleUserName: string = "";

  constructor(data: Partial<ClientData> = {}) {
    Object.assign(this, { ...defaultClientData, ...data });
  }

  static async save(model: ClientData): Promise<EntityId | void> {
    if (model.id) {
      return (await httpClient.put<EntityId>(`${CLIENTS_URL}/${model.id}`, model)).data;
    } else {
      return (await httpClient.post<EntityId>(CLIENTS_URL, model)).data;
    }
  }

  static async findOne(id: string): Promise<ClientData> {
    return await httpClient
      .get<ClientData>(`${CLIENTS_URL}/${id}`)
      .then(response => response.data);
  }

  static async delete(id: string): Promise<void> {
    await httpClient.delete(`${CLIENTS_URL}/${id}`);
  }

  private static serializeParams(params: any) {
    return qs.stringify(params, {
      serializeDate: (date: Date) => format(date, "yyyy-MM-dd"),
    });
  }

  static async clientList(
    filter: ClientFilter,
    page: PageRequest
  ): Promise<PageResponse<ClientData>> {
    return await httpClient
      .get<PageResponse<ClientData>>(CLIENTS_URL, {
        params: { ...filter, ...page },
        paramsSerializer: this.serializeParams,
      })
      .then(response => response.data);
  }

  static async emailClientList(
    filter: ClientFilter,
    page: PageRequest
  ): Promise<PageResponse<ClientData>> {
    return await httpClient
      .get<PageResponse<ClientData>>("/emails/clients", {
        params: { ...filter, ...page },
        paramsSerializer: this.serializeParams,
      })
      .then(response => response.data);
  }

  static async count(filter: ClientFilter): Promise<number> {
    return await httpClient
      .get<number>(`${CLIENTS_URL}/count`, {
        params: filter,
        paramsSerializer: this.serializeParams,
      })
      .then(response => response.data);
  }
}

export { ClientModel };
export default ClientModel;
